import React from 'react';

import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { updatePlacementKeyValueById } from '@client/core/atoms/placements.js';
import { Performance } from '@client/core/utils/Performance.js';
import { i18n } from '@lingui/core';
import {
  debugLog,
  type PlacementId,
  PlacementStatus
} from '@schibsted-nmp/advertising-shared';

import type { RetailMediaProduct } from '../types/index.js';
import {
  trackClickBackend,
  trackImpressionBackend,
  trackViewableImpressionBackend
} from '../utils/RetailMediaApi.js';
import { SponsoredProductAdPulseTracker } from '../utils/RetailMediaPulse.js';

export function SponsoredProductCarousel({
  products,
  placementId,
  categories
}: SponsoredProductCarousel.Props) {
  if (!products.length) {
    debugLog(
      'retail_media -> Render failed due to missing products for placement:',
      placementId
    );

    updateMetricByKey(placementId, PlacementStatus.AdNoFill);
    updatePlacementKeyValueById(placementId, 'status', 'error');

    return null;
  }

  return (
    <SponsoredProductCarousel.Inner
      products={products}
      placementId={placementId}
      categories={categories}
    />
  );
}

export namespace SponsoredProductCarousel {
  export type Props = {
    products: [RetailMediaProduct, ...Array<RetailMediaProduct>];
    placementId: PlacementId;
    categories?: string[] | undefined;
  };

  export function Inner({
    products,
    placementId,
    categories
  }: SponsoredProductCarousel.Props) {
    const carouselRef = React.useRef<HTMLDivElement>(null);
    const locale = Locale.getCurrent();
    const [{ advertisingLabel }] = products;

    useImpressionHandler(carouselRef, placementId);
    useViewableImpressionHandler(carouselRef, placementId);

    const productLevelTracking = ProductLevelTracking.use(
      placementId,
      categories
    );

    return (
      <section
        ref={carouselRef}
        id={placementId}
        className="rm-carousel"
        lang={locale}
        aria-roledescription="carousel"
        aria-label={advertisingLabel || 'Sponsored products'}
      >
        {advertisingLabel ? (
          <div className="rm-carousel__label">{advertisingLabel}</div>
        ) : null}

        <div className="rm-carousel__frame">
          <Pagination {...{ products }}>
            {(product) => (
              <SponsoredProductCarousel.Card
                key={product.adId}
                product={product}
                locale={locale}
                {...productLevelTracking}
              />
            )}
          </Pagination>
        </div>
      </section>
    );
  }

  export function Pagination({ products, children }: Pagination.Props) {
    const pageSize = Pagination.PageSize.use();
    const [currentPage, setCurrentPage] = React.useState(0);

    const pages = React.useMemo(
      () => Pagination.chunk(products, pageSize),
      [products, pageSize]
    );

    const pageCount = Math.max(pages.length, 1);

    // A wider viewport means fewer pages - don't strand the user past the end.
    React.useEffect(() => {
      setCurrentPage((currentPage) => Math.min(currentPage, pageCount - 1));
    }, [pageCount]);

    const goToPreviousPage = React.useCallback(() => {
      setCurrentPage((currentPage) => Math.max(0, currentPage - 1));
    }, [setCurrentPage]);

    const goToNextPage = React.useCallback(() => {
      setCurrentPage((currentPage) => Math.min(pageCount - 1, currentPage + 1));
    }, [setCurrentPage, pageCount]);

    const previousPageIsDisabled = currentPage === 0;
    const nextPageIsDisabled = currentPage >= pageCount - 1;

    return (
      <>
        <button
          type="button"
          className="rm-carousel__nav rm-carousel__nav--prev"
          onClick={goToPreviousPage}
          disabled={previousPageIsDisabled}
          aria-label="Previous"
        >
          <Pagination.ChevronLeftIcon />
        </button>

        <div className="rm-carousel__viewport">
          <div
            className="rm-carousel__track"
            style={{ transform: `translateX(-${currentPage * 100}%)` }}
          >
            {pages.map((pageProducts, pageIndex) => (
              <div
                // Pages are a pure slice of `products`; index is the identity.
                key={`page-${pageIndex}`}
                className="rm-carousel__page"
                style={{ '--rm-per-page': pageSize } as React.CSSProperties}
                // Keep off-screen pages out of tab order (React 18 lacks an `inert` prop)
                {...(pageIndex !== currentPage && { inert: '' })}
              >
                {pageProducts.map(children)}
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          className="rm-carousel__nav rm-carousel__nav--next"
          onClick={goToNextPage}
          disabled={nextPageIsDisabled}
          aria-label="Next"
        >
          <Pagination.ChevronRightIcon />
        </button>
      </>
    );
  }

  export namespace Pagination {
    export type Props = {
      products: [RetailMediaProduct, ...Array<RetailMediaProduct>];
      children: (product: RetailMediaProduct) => React.ReactNode;
    };

    export function chunk<T>(items: Array<T>, size: number): Array<Array<T>> {
      const pages = new Array<Array<T>>();

      for (let index = 0; index < items.length; index += size) {
        pages.push(items.slice(index, index + size));
      }

      return pages;
    }

    export namespace PageSize {
      /**
       * How many cards are visible at once. The carousel pages by a whole screenful,
       * so this also determines the number of pages.
       */
      const BREAKPOINTS = [
        { query: '(min-width: 768px)', pageSize: 3 }
      ] as const;

      const MIN_PAGE_SIZE = 2;

      export function resolve() {
        if (typeof window === 'undefined') return MIN_PAGE_SIZE;

        const match = BREAKPOINTS.find(
          ({ query }) => window.matchMedia(query).matches
        );

        return match?.pageSize ?? MIN_PAGE_SIZE;
      }

      export function use() {
        const [pageSize, setPageSize] = React.useState(resolve);

        React.useEffect(() => {
          const queries = BREAKPOINTS.map(({ query }) =>
            window.matchMedia(query)
          );

          const onChange = () => {
            setPageSize(resolve());
          };

          queries.forEach((query) => {
            query.addEventListener('change', onChange);
          });

          return () => {
            queries.forEach((query) => {
              query.removeEventListener('change', onChange);
            });
          };
        }, []);

        return pageSize;
      }
    }

    export function ChevronLeftIcon() {
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M15 5.25 8.25 12 15 18.75"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }

    export function ChevronRightIcon() {
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M9 5.25 15.75 12 9 18.75"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
  }

  export namespace ProductLevelTracking {
    export function use(
      placementId: PlacementId,
      categories: string[] | undefined
    ) {
      const trackingContext = TrackingContext.use(placementId, categories);

      return {
        onImpression: useImpressionHandler(trackingContext),
        onViewableImpression: useViewableImpressionHandler(trackingContext),
        onClick: useClickHandler(trackingContext),
        onInfoPageClick: useInfoPageClickHandler(trackingContext)
      } as const;
    }

    export type TrackingContext = ReturnType<typeof TrackingContext.use>;

    export namespace TrackingContext {
      export function use(
        placementId: PlacementId,
        categories: string[] | undefined
      ) {
        return React.useMemo(
          () =>
            ({
              zone: 'recommerce_search',
              categories,
              placementId
            }) as const,
          [categories, placementId]
        );
      }
    }

    export function useImpressionHandler(trackingContext: TrackingContext) {
      /**
       * Crossing a breakpoint re-chunks the products into differently keyed pages,
       * which remounts the cards that moved and resets their local `hasTrackedView`.
       * Dedupe by ad id here, where the state survives a card remount.
       */
      const trackedImpressionIds = React.useRef(new Set<number>());

      return React.useCallback(
        (product: RetailMediaProduct) => {
          if (trackedImpressionIds.current.has(product.adId)) return;
          trackedImpressionIds.current.add(product.adId);

          // Track server-side to avoid ad blockers
          void trackImpressionBackend(product.impressionUrl);

          void SponsoredProductAdPulseTracker.instance.trackImpression({
            product,
            ...trackingContext
          });
        },
        [trackingContext]
      );
    }

    export function useViewableImpressionHandler(
      trackingContext: TrackingContext
    ) {
      /**
       * Crossing a breakpoint re-chunks the products into differently keyed pages,
       * which remounts the cards that moved and resets their local `hasTrackedView`.
       * Dedupe by ad id here, where the state survives a card remount.
       */
      const trackedViewableImpressionIds = React.useRef(new Set<number>());

      return React.useCallback(
        (product: RetailMediaProduct) => {
          if (trackedViewableImpressionIds.current.has(product.adId)) return;
          trackedViewableImpressionIds.current.add(product.adId);

          if (product.viewableImpressionUrl) {
            // Track viewable impression server-side to avoid ad blockers
            void trackViewableImpressionBackend(product.viewableImpressionUrl);
          }

          void SponsoredProductAdPulseTracker.instance.trackViewableImpression({
            product,
            ...trackingContext
          });
        },
        [trackingContext]
      );
    }

    export function useClickHandler(trackingContext: TrackingContext) {
      return React.useCallback(
        (product: RetailMediaProduct) => {
          // Track click server-side to avoid ad blockers
          void trackClickBackend(product.clickUrl);

          void SponsoredProductAdPulseTracker.instance.trackClick({
            product,
            ...trackingContext
          });
        },
        [trackingContext]
      );
    }

    export function useInfoPageClickHandler(trackingContext: TrackingContext) {
      return React.useCallback(
        (product: RetailMediaProduct) => {
          void SponsoredProductAdPulseTracker.instance.trackInfoPageClick({
            product,
            ...trackingContext
          });
        },
        [trackingContext]
      );
    }
  }

  export function useImpressionHandler(
    adRef: React.RefObject<HTMLDivElement>,
    placementId: PlacementId
  ) {
    const hasTrackedImpression = React.useRef(false);

    const impressionHasTriggered = Performance.useImpression({
      elementRef: adRef
    });

    React.useEffect(() => {
      if (hasTrackedImpression.current) return;
      if (!impressionHasTriggered) return;

      hasTrackedImpression.current = true;

      updateMetricByKey(placementId, PlacementStatus.AdLoaded);
      updatePlacementKeyValueById(placementId, 'status', 'loaded');

      // TODO: track carousel-level impression using Pulse
    }, [impressionHasTriggered, placementId]);
  }

  export function useViewableImpressionHandler(
    adRef: React.RefObject<HTMLDivElement>,
    placementId: PlacementId
  ) {
    const hasTrackedViewableImpression = React.useRef(false);

    const viewableImpressionHasTriggered = Performance.useViewableImpression({
      elementRef: adRef
    });

    React.useEffect(() => {
      if (hasTrackedViewableImpression.current) return;
      if (!viewableImpressionHasTriggered) return;

      hasTrackedViewableImpression.current = true;

      updateMetricByKey(placementId, PlacementStatus.AdViewed);
      // TODO: Ensure viewable slots are rendered the same as loaded slots before enabling this:
      // updatePlacementKeyValueById(placementId, 'status', 'viewable');

      // TODO: track carousel-level viewable impression using Pulse
    }, [viewableImpressionHasTriggered, placementId]);
  }

  /**
   * Each card tracks its own impression and viewable impression, and the two are
   * not the same event: an impression means rendered, so every product in the
   * response fires one on mount whether or not the user pages to it. Only the
   * viewable impression is gated on being seen, and there each card owns its own
   * observer - cards on later pages are clipped by the viewport's
   * `overflow: hidden`, so they stay unintersected until the user pages to them.
   */
  export function Card({
    product,
    locale,
    onImpression,
    onViewableImpression,
    onClick,
    onInfoPageClick
  }: Card.Props) {
    const cardRef = React.useRef<HTMLDivElement>(null);

    Card.useImpressionHandler(cardRef, product, onImpression);
    Card.useViewableImpressionHandler(cardRef, product, onViewableImpression);

    const handleClick = Card.useClickHandler(product, onClick);

    const handleInfoPageClick = Card.useInfoPageClickHandler(
      product,
      onInfoPageClick
    );

    return (
      <div className="rm-card" ref={cardRef}>
        <a
          className="rm-card__link"
          href={product.productUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
        >
          <div className="rm-card__image-wrap">
            <img
              className="rm-card__image"
              src={product.imageUrl}
              alt=""
              loading="lazy"
            />
          </div>

          <div className="rm-card__body">
            <span className="rm-card__price">
              {Locale.formatPrice(product, locale)}
            </span>

            <span className="rm-card__title">{product.title}</span>

            <span className="rm-card__advertiser">
              <span className="rm-card__advertiser-name">
                {product.advertiser}
              </span>

              <Card.ExternalLinkIcon />
            </span>
          </div>
        </a>

        <a
          className="rm-card__dsa"
          href={product.dsaUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleInfoPageClick}
          title="Ad info"
          aria-label="Ad info"
        >
          <Card.DotsVerticalIcon />
        </a>
      </div>
    );
  }

  export namespace Card {
    export type Props = {
      product: RetailMediaProduct;
      locale: string;
      onImpression: (product: RetailMediaProduct) => void;
      onViewableImpression: (product: RetailMediaProduct) => void;
      onClick: (product: RetailMediaProduct) => void;
      onInfoPageClick: (product: RetailMediaProduct) => void;
    };

    export function useImpressionHandler(
      cardRef: React.RefObject<HTMLDivElement>,
      product: RetailMediaProduct,
      onImpression: (product: RetailMediaProduct) => void
    ) {
      const hasTrackedImpression = React.useRef(false);

      const impressionHasTriggered = Performance.useImpression({
        elementRef: cardRef
      });

      React.useEffect(() => {
        if (hasTrackedImpression.current) return;
        if (!impressionHasTriggered) return;

        hasTrackedImpression.current = true;
        onImpression(product);
      }, [impressionHasTriggered, product, onImpression]);
    }

    export function useViewableImpressionHandler(
      cardRef: React.RefObject<HTMLDivElement>,
      product: RetailMediaProduct,
      onViewableImpression: (product: RetailMediaProduct) => void
    ) {
      const hasTrackedViewableImpression = React.useRef(false);

      const viewableImpressionHasTriggered = Performance.useViewableImpression({
        elementRef: cardRef
      });

      React.useEffect(() => {
        if (hasTrackedViewableImpression.current) return;
        if (!viewableImpressionHasTriggered) return;

        hasTrackedViewableImpression.current = true;
        onViewableImpression(product);
      }, [viewableImpressionHasTriggered, product, onViewableImpression]);
    }

    export function useClickHandler(
      product: RetailMediaProduct,
      onClick: (product: RetailMediaProduct) => void
    ) {
      return React.useCallback(() => {
        onClick(product);
      }, [product, onClick]);
    }

    export function useInfoPageClickHandler(
      product: RetailMediaProduct,
      onInfoPageClick: (product: RetailMediaProduct) => void
    ) {
      return React.useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
          // The DSA link sits inside the ad card, so without this the card's own
          // handler counts opening the ad info page as a click on the product
          event.stopPropagation();

          onInfoPageClick(product);
        },
        [product, onInfoPageClick]
      );
    }

    export function DotsVerticalIcon() {
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M8 4C7.31 4 6.75 3.44 6.75 2.75S7.31 1.5 8 1.5s1.25.56 1.25 1.25S8.69 4 8 4Z"
            fill="currentColor"
          />
          <path
            d="M8 9.25C7.31 9.25 6.75 8.69 6.75 8S7.31 6.75 8 6.75 9.25 7.31 9.25 8 8.69 9.25 8 9.25Z"
            fill="currentColor"
          />
          <path
            d="M8 14.5c-.69 0-1.25-.56-1.25-1.25S7.31 12 8 12s1.25.56 1.25 1.25S8.69 14.5 8 14.5Z"
            fill="currentColor"
          />
        </svg>
      );
    }

    export function ExternalLinkIcon() {
      return (
        <svg
          className="rm-card__external-icon"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6 3H3V13H13V10M10 3H13M13 3V6M13 3L6 10"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
  }

  export namespace Locale {
    /** Locales BapSearchAd-free formatting can rely on; anything else falls back. */
    const SUPPORTED = ['fi', 'nb', 'da', 'sv'];
    const FALLBACK = 'nb';

    export function getCurrent() {
      return SUPPORTED.includes(i18n.locale) ? i18n.locale : FALLBACK;
    }

    /**
     * Replaces the DOM-level decimal-separator workaround the single-product ad
     * needed: Intl already formats per locale, so `fi` gets its comma for free.
     */
    export function formatPrice(
      product: RetailMediaProduct,
      locale: string
    ): string {
      return `${new Intl.NumberFormat(locale).format(product.price)} ${product.priceUnit}`;
    }
  }
}
